import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCompany } from '@/contexts/CompanyContext';
import { useDirection } from '@/contexts/DirectionContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { searchApi } from '../api/search.api';
import type { SearchResponse } from '../interfaces/search.interface';

const DEFAULT_DEBOUNCE = 180;

/** Mic debounce hook intern — evită un fișier suplimentar. */
function useDebounced<T>(value: T, delay = DEFAULT_DEBOUNCE): T {
  const [v, setV] = useState(value);
  const tRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => setV(value), delay);
    return () => {
      if (tRef.current) clearTimeout(tRef.current);
    };
  }, [value, delay]);
  return v;
}

interface UseSearchOptions {
  enabled: boolean;
  query: string;
}

interface UseSearchResult {
  data: SearchResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
}

/**
 * Search hook cu debouncing intern (180ms) și keying pe companyId + lang.
 * `enabled` controlează dacă request-ul rulează (ex. doar când modal-ul e deschis).
 */
export function useSearch({ enabled, query }: UseSearchOptions): UseSearchResult {
  const { selectedCompanyId } = useCompany();
  const { direction } = useDirection();
  const { currentLanguage } = useLanguage();
  const companyId = typeof selectedCompanyId === 'number' ? selectedCompanyId : undefined;
  const debounced = useDebounced(query.trim(), DEFAULT_DEBOUNCE);

  const q = useQuery<SearchResponse>({
    queryKey: ['search', debounced, selectedCompanyId, currentLanguage, direction],
    queryFn: () => searchApi.search(debounced, currentLanguage, companyId, direction),
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    // Păstrăm rezultatele anterioare ca să nu se goleze lista la fiecare
    // tastă apăsată (UX mai stabil).
    placeholderData: (prev) => prev,
  });

  return { data: q.data, isLoading: q.isLoading, isFetching: q.isFetching };
}
